import { useEffect, useState } from "react";
import { Building2, Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Units() {
  const { user } = useAuth();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ unit_code: "", unit_name: "", location: "", address: "" });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/units");
      setUnits(r.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role]);

  if (user?.role !== "admin") {
    return <div className="p-6 text-slate-500">Units management is admin-only.</div>;
  }

  const create = async () => {
    if (!form.unit_code || !form.unit_name) { toast.error("Code and name required"); return; }
    setSaving(true);
    try {
      await api.post("/units", form);
      toast.success("Unit created");
      setForm({ unit_code: "", unit_name: "", location: "", address: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const remove = async (u) => {
    const extraWarn = u.is_default
      ? "\n\nThis IS the DEFAULT unit. Once deleted, it will NOT be auto-recreated on next startup (Round 22 behaviour). Make sure no NPDs / projects / assemblies / RFQs / users are still assigned to it."
      : "";
    if (!window.confirm(`Delete unit ${u.unit_code} (${u.unit_name})?${extraWarn}\n\nAll assigned records must be reassigned first — the backend will reject the delete otherwise.`)) return;
    try { await api.delete(`/units/${u.unit_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const startEdit = (u) => setEditing({ ...u });
  const cancelEdit = () => setEditing(null);

  const saveEdit = async () => {
    if (!editing.unit_name?.trim()) { toast.error("Name required"); return; }
    if (!editing.is_default && !editing.unit_code?.trim()) { toast.error("Code required"); return; }
    setSavingEdit(true);
    try {
      const payload = {
        unit_name: editing.unit_name.trim(),
        location: editing.location || "",
        address: editing.address || "",
      };
      if (!editing.is_default) {
        payload.unit_code = editing.unit_code.trim().toUpperCase();
      }
      await api.put(`/units/${editing.unit_id}`, payload);
      toast.success("Unit updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="units-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Building2 size={22} className="text-orange-600" /> Units (Plants)
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage manufacturing units. Each non-admin user and every costing belongs to one unit. Admin sees everything across units.</p>
      </div>

      {/* Create form */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Add New Unit</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input value={form.unit_code} onChange={(e) => setForm({ ...form, unit_code: e.target.value.toUpperCase() })}
                 placeholder="Code (e.g. P1)" className="input" maxLength={6} data-testid="unit-code" />
          <input value={form.unit_name} onChange={(e) => setForm({ ...form, unit_name: e.target.value })}
                 placeholder="Name (e.g. Plant 1)" className="input" data-testid="unit-name" />
          <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
                 placeholder="Location (optional)" className="input" />
          <button onClick={create} disabled={saving}
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-semibold rounded-md px-4 py-2 flex items-center justify-center gap-1.5"
                  data-testid="unit-create">
            <Plus size={14} /> {saving ? "Saving…" : "Add Unit"}
          </button>
        </div>
        <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
               placeholder="Address (optional)" className="input mt-2" />
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400">Loading…</div>
        ) : units.length === 0 ? (
          <div className="p-10 text-center text-slate-400">No units yet.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] text-sm" data-testid="units-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Location</th>
                <th className="text-left p-3">Address</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {units.map((u) => {
                const isEditing = editing?.unit_id === u.unit_id;
                if (isEditing) {
                  return (
                    <tr key={u.unit_id} className="bg-orange-50" data-testid={`unit-edit-row-${u.unit_code}`}>
                      <td className="p-2 align-top">
                        <input
                          value={editing.unit_code || ""}
                          onChange={(e) => setEditing({ ...editing, unit_code: e.target.value.toUpperCase() })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm font-mono disabled:bg-slate-100 disabled:text-slate-400"
                          maxLength={6}
                          disabled={u.is_default}
                          placeholder="Code"
                          data-testid="edit-unit-code"
                        />
                        {u.is_default && (
                          <div className="text-[10px] text-slate-500 mt-1">Default code is locked</div>
                        )}
                        {!u.is_default && editing.unit_code !== u.unit_code && (
                          <div className="text-[10px] text-amber-700 mt-1">
                            ⚠ Existing NPDs keep <span className="font-mono">{u.unit_code}-</span> prefix
                          </div>
                        )}
                      </td>
                      <td className="p-2 align-top">
                        <input
                          value={editing.unit_name || ""}
                          onChange={(e) => setEditing({ ...editing, unit_name: e.target.value })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                          placeholder="Name"
                          data-testid="edit-unit-name"
                        />
                      </td>
                      <td className="p-2 align-top">
                        <input
                          value={editing.location || ""}
                          onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                          placeholder="Location"
                        />
                      </td>
                      <td className="p-2 align-top">
                        <input
                          value={editing.address || ""}
                          onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                          className="w-full px-2 py-1 border border-slate-300 rounded text-sm"
                          placeholder="Address"
                        />
                      </td>
                      <td className="p-2 text-right whitespace-nowrap align-top">
                        <button
                          onClick={saveEdit}
                          disabled={savingEdit}
                          className="text-emerald-600 hover:text-emerald-700 disabled:text-slate-300 mr-3"
                          title="Save"
                          data-testid="edit-unit-save"
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={cancelEdit}
                          disabled={savingEdit}
                          className="text-slate-400 hover:text-slate-600"
                          title="Cancel"
                          data-testid="edit-unit-cancel"
                        >
                          <X size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={u.unit_id} className="hover:bg-slate-50" data-testid={`unit-row-${u.unit_code}`}>
                    <td className="p-3 font-mono text-sm font-semibold">
                      {u.unit_code}
                      {u.is_default && <span className="ml-2 text-[10px] bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">DEFAULT</span>}
                    </td>
                    <td className="p-3">{u.unit_name}</td>
                    <td className="p-3 text-slate-600">{u.location || "—"}</td>
                    <td className="p-3 text-slate-600 truncate max-w-xs">{u.address || "—"}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => startEdit(u)}
                        className="text-slate-400 hover:text-orange-600 mr-3"
                        title="Edit"
                        data-testid={`unit-edit-${u.unit_code}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => remove(u)}
                        className="text-slate-400 hover:text-red-600"
                        title={u.is_default ? "Delete (DEF — will not be auto-recreated)" : "Delete"}
                        data-testid={`unit-delete-${u.unit_code}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
